import { Edit } from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import MultipleStopOutlinedIcon from '@mui/icons-material/MultipleStopOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, Modal, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { Loading } from "../../components/Loading";
import { MyConfirmDialog } from "../../components/MyConfirmDialog";
import { MyRenderCell } from "../../components/MyRenderCell";
import { createEntityToDisable } from "../../models/entityToDisable";
import { IMaterial, createMaterial } from "../../models/materials";
import { addMaterial, getMaterials, removeMaterial, reset } from "../../redux";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from '../../redux/store';
import { MyBoxModal } from "../../styled-components";
import { MyForm } from './components';
import { MyAdjustmentForm } from "./components/MyAdjustmentForm";

interface Props {
    entity: string;
    idParent?: string;
    isToAddMaterials?: boolean;
}

export const Materials = ({ isToAddMaterials, entity, idParent }: Props) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const openDialog = () => setDialogOpen(!dialogOpen);

    const [adjustOpen, setAdjustpen] = useState(false);
    const openAdjustDialog = () => {
        setAdjustpen(!adjustOpen);
        dispatch(reset() as any);
    }

    const [entityToDisable, setEntityToDisable] = useState(createEntityToDisable({}));

    const [editing, setEditing] = useState(false);

    const [confirmAddDialog, setConfirmAdd] = useState(false);
    const openConfirmAddDialog = () => setConfirmAdd(!confirmAddDialog);

    const [confirmRemoveDialog, setConfirmRemove] = useState(false);
    const openConfirmRemoveDialog = () => setConfirmRemove(!confirmRemoveDialog);

    const [materialToEdit, setMaterialToEmpty] = useState(createMaterial({}));

    const [pageSize, setPageSize] = useState<number>(15);

    const dispatch = useAppDispatch();
    const { isLoading, materials = [], error } = useAppSelector((state: RootState) => state.materials);

    useEffect(() => {
        dispatch(getMaterials(entity, idParent) as any);
    }, [dispatch, entity, idParent])

    const onEdit = (material: IMaterial) => {
        setMaterialToEmpty(createMaterial(material));
        setEditing(true);
        openDialog();
    }

    const onCreate = () => {
        setMaterialToEmpty(createMaterial({}));
        setEditing(false);
        openDialog();
    }

    const onAdjustment = (material: IMaterial) => {
        setMaterialToEmpty(createMaterial(material));
        setAdjustpen(true)
    }

    const onAddMaterial = (id: number, quantity?: number) => {
        if (idParent)
            dispatch(addMaterial(id, entity, idParent, quantity) as any);
    }

    const onRemoveMaterial = (id: number) => {
        if (idParent)
            dispatch(removeMaterial(id, entity, idParent) as any);
    }

    const onConfirmAdd = (material: IMaterial) => {
        setEntityToDisable(createEntityToDisable(material));
        openConfirmAddDialog();
    }

    const onConfirmRemove = (material: IMaterial) => {
        setEntityToDisable(createEntityToDisable(material));
        openConfirmRemoveDialog();
    }

    const Columns: GridColDef[] = [
        {
            field: "action",
            headerName: "Actions",
            width: isToAddMaterials ? 140 : 140,
            sortable: false,
            align: "right",
            renderCell: (params) => {
                const onClick = (e: React.MouseEvent<HTMLElement>) => {
                    onEdit(params.row as any);
                };
                const addMaterial = (e: React.MouseEvent<HTMLElement>) => {
                    onConfirmAdd(params.row as any);
                };
                const removeMaterial = (e: React.MouseEvent<HTMLElement>) => {
                    onConfirmRemove(params.row as any);
                };
                const adjustment = (e: React.MouseEvent<HTMLElement>) => {
                    onAdjustment(params.row as any);
                };
                return (
                    <>
                        {!isToAddMaterials && (<>
                            <Tooltip title="Edit" >
                                <Button onClick={onClick}><Edit /></Button>
                            </Tooltip>
                        </>)}
                        {isToAddMaterials && ((params.row.subActivityId || params.row.courseId || params.row.groupId) ?
                            (<Tooltip title="Remove Material" >
                                <Button onClick={removeMaterial}><RemoveIcon /></Button>
                            </Tooltip>
                            ) :
                            (<Tooltip title="Add Material" >
                                <Button onClick={addMaterial}><AddIcon /></Button>
                            </Tooltip>
                            ))
                        }
                        {<Tooltip title="Adjust Inventory">
                            <Button onClick={adjustment}><MultipleStopOutlinedIcon /></Button>
                        </Tooltip>}
                    </>
                )
            }
        },
        {
            field: 'isbn',
            headerName: 'ISBN',
            width: 200,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'name',
            headerName: 'Name',
            width: 500,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'quantity',
            headerName: 'Quantity',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'availableQuantity',
            headerName: 'Avalible Quantity',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'isbn10',
            headerName: 'ISBN 10',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'author',
            headerName: 'Author',
            width: 250,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'edition',
            headerName: 'Edition',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'publisher',
            headerName: 'Publisher',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'discountId',
            headerName: 'Discount Id',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'weight',
            headerName: 'Weight',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'length',
            headerName: 'Length',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'width',
            headerName: 'Width',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'copyright',
            headerName: 'Copyright',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'custom',
            headerName: 'Custom',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'nationalISBN',
            headerName: 'National ISBN',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'startDate',
            headerName: 'Start Date',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        }
    ]

    return (
        <div>
            <Loading isLoading={isLoading} />
            {!isToAddMaterials && (
                <>
                    <Typography variant="h4" component="h1">Materials</Typography>
                    <br />
                    <Button onClick={onCreate}>Add</Button>
                </>
            )}
            <div style={{ height: "calc(100vh - 250px)", width: '100%', flexGrow: 1 }}>
                <DataGrid
                    getRowId={(row: IMaterial) => row.isbn}
                    rows={materials}
                    columns={Columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[15, 50, 100]}
                    columnVisibilityModel={{
                        quantity: idParent !== undefined
                    }}
                //checkboxSelection
                />
            </div>
            <Modal open={dialogOpen} onClose={openDialog}>
                <MyBoxModal>
                    <MyForm onCancel={openDialog} material={materialToEdit} editing={editing} />
                </MyBoxModal>
            </Modal>
            <Modal open={adjustOpen} onClose={openAdjustDialog}>
                <MyBoxModal>
                    <MyAdjustmentForm onCancel={openAdjustDialog} material={materialToEdit} error={error} />
                </MyBoxModal>
            </Modal>
            <Modal open={confirmAddDialog} onClose={openConfirmAddDialog}>
                <MyBoxModal>
                    <MyConfirmDialog onCancel={openConfirmAddDialog} entity={entityToDisable} onOk={onAddMaterial} isAdding={true} />
                </MyBoxModal>
            </Modal>
            <Modal open={confirmRemoveDialog} onClose={openConfirmRemoveDialog}>
                <MyBoxModal>
                    <MyConfirmDialog onCancel={openConfirmRemoveDialog} entity={entityToDisable} onOk={onRemoveMaterial} />
                </MyBoxModal>
            </Modal>
        </div >
    )
}
